// src/data/topicSession.js
// Ablauf einer Themen-Woche (Zahlen, Wochentage, Uhrzeit …), ohne React:
//   1. 2–4 neue Einträge eines Blocks kennenlernen
//   2. 2–4 Abfragen, gemischt aus allem bisher Gelernten
//      (neu: Auswahl, danach Eintippen)
//   3. nach jeweils 10 Abfragen ein großer Test über einen Block, dessen
//      Einträge alle schon vorgestellt wurden (alles eintippen)
//   4. sind alle Blöcke bestanden: nach 10 weiteren Abfragen der Abschluss-Test
// Alle Funktionen geben ein neues Session-Objekt zurück.

export const TRIES_BEFORE_TEST = 10;
const MAX_BATCH = 4;
const MIN_ROUND = 2;
const MAX_ROUND = 4;
// höchstens so viele Fehler im großen Test (mindestens 1, bei langen Tests 10 %)
export function allowedTestErrors(count) {
  return Math.max(1, Math.floor(count / 10));
}

// Stufen: 0 = gerade kennengelernt (Auswahl), 1 = Eintippen, 2 = sicher
const MAX_LEVEL = 2;

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Größe des nächsten Kennenlern-Pakets: Rest eines Blocks gleichmäßig in
// Pakete von 2–4 teilen (6 → 3+3, 10 → 4+3+3, 5 → 3+2)
export function nextBatchSize(remaining) {
  if (remaining <= MAX_BATCH) return remaining;
  const batches = Math.ceil(remaining / MAX_BATCH);
  return Math.ceil(remaining / batches);
}

/**
 * items: alle Themen-Einträge ({ id, topic, target, src }) in CSV-Reihenfolge;
 * genommen werden die, deren topic in einem Block der Woche steht.
 * passedBlocks: Schlüssel bereits bestandener Blöcke (z. B. aus einer
 * früheren Sitzung), deren Einträge gelten als bekannt.
 */
export function startTopicSession(weekPlan, items, passedBlocks = [], finalPassed = false) {
  const blockKeys = weekPlan.blocks.map((b) => b.key);
  const blockOfTopic = {};
  weekPlan.blocks.forEach((b) => b.topics.forEach((t) => (blockOfTopic[t] = b.key)));

  const session = {
    blockKeys,
    items: items
      .filter((it) => blockOfTopic[it.topic])
      .map((it) => {
        const blockKey = blockOfTopic[it.topic];
        const known = passedBlocks.includes(blockKey);
        return {
          ...it,
          block: blockKey,
          introduced: known,
          level: known ? MAX_LEVEL : 0,
          wrong: false,
        };
      }),
    passed: blockKeys.filter((k) => passedBlocks.includes(k)),
    finalPassed,
    tries: 0,
    mode: "intro",
    queue: [],
    pos: 0,
    lastBatch: [],
    testBlock: null,
  };

  // Alle Blöcke schon bestanden: gleich zum Abschluss-Test
  if (session.passed.length === blockKeys.length && !finalPassed) {
    return startTest(session, "final");
  }
  return nextStep(session);
}

function introducedIndices(session) {
  return session.items
    .map((it, idx) => (it.introduced ? idx : null))
    .filter((idx) => idx !== null);
}

// Einträge eines Blocks ("final" = alle der Woche)
function blockIndices(session, blockKey) {
  return session.items
    .map((it, idx) => (blockKey === "final" || it.block === blockKey ? idx : null))
    .filter((idx) => idx !== null);
}

function startTest(session, blockKey) {
  return {
    ...session,
    mode: "test",
    testBlock: blockKey,
    queue: shuffle(blockIndices(session, blockKey)),
    pos: 0,
  };
}

// Abfrage-Runde: zuerst Falsches und gerade Gelerntes, dazu möglichst ein
// älterer Eintrag; Reihenfolge gemischt
function startQuizRound(session, size) {
  const pool = introducedIndices(session);
  const roundSize = Math.min(pool.length, Math.max(MIN_ROUND, Math.min(MAX_ROUND, size)));
  const isLatest = (idx) => session.lastBatch.includes(idx);
  const priority = (idx) => {
    const it = session.items[idx];
    if (it.wrong) return 0;
    if (it.level === 0) return isLatest(idx) ? 1 : 2;
    return 2 + it.level;
  };
  const sorted = shuffle(pool).sort((a, b) => priority(a) - priority(b));

  let round = sorted.slice(0, roundSize);
  const older = sorted.filter((idx) => !isLatest(idx) && !round.includes(idx));
  if (roundSize >= MIN_ROUND && round.every(isLatest) && older.length > 0) {
    round = [...round.slice(0, roundSize - 1), older[0]];
  }

  return { ...session, mode: "quiz", queue: shuffle(round), pos: 0 };
}

// Was kommt nach einem Kennenlern-Paket, einer Runde oder einem Test?
function nextStep(session) {
  if (session.finalPassed) return { ...session, mode: "done", queue: [], pos: 0 };

  const allPassed = session.passed.length === session.blockKeys.length;

  if (session.tries >= TRIES_BEFORE_TEST) {
    if (allPassed) return startTest(session, "final");

    // Block, dessen Einträge alle vorgestellt sind, mit dem besten Wissensstand
    const avgLevel = (key) => {
      const idxs = blockIndices(session, key);
      return idxs.reduce((sum, i) => sum + session.items[i].level, 0) / idxs.length;
    };
    const eligible = session.blockKeys.filter(
      (key) =>
        !session.passed.includes(key) &&
        blockIndices(session, key).every((i) => session.items[i].introduced)
    );
    if (eligible.length > 0) {
      const best = eligible.reduce((a, b) => (avgLevel(b) > avgLevel(a) ? b : a));
      return startTest(session, best);
    }
  }

  // Neue Einträge: aus dem ersten Block, der noch Unbekanntes hat
  const nextBlock = session.blockKeys.find((key) =>
    blockIndices(session, key).some((i) => !session.items[i].introduced)
  );
  if (nextBlock) {
    const remaining = blockIndices(session, nextBlock).filter(
      (i) => !session.items[i].introduced
    );
    const batch = remaining.slice(0, nextBatchSize(remaining.length));
    return { ...session, mode: "intro", queue: batch, pos: 0, lastBatch: batch };
  }

  return startQuizRound({ ...session, lastBatch: [] }, 3);
}

function advance(session) {
  if (session.pos + 1 < session.queue.length) {
    return { ...session, pos: session.pos + 1 };
  }
  if (session.mode === "intro") {
    return startQuizRound(session, session.lastBatch.length);
  }
  return nextStep(session);
}

/** Aktueller Eintrag (Kennenlernen/Abfrage) als Index in session.items */
export function currentItemIndex(session) {
  return session.queue[session.pos] ?? null;
}

/** Abfrageart des aktuellen Eintrags: "choice" (Auswahl) oder "type" (Eintippen) */
export function questionKind(session) {
  const idx = currentItemIndex(session);
  return idx !== null && session.items[idx].level === 0 ? "choice" : "type";
}

/** Kennenlern-Karte bestätigt */
export function confirmIntro(session) {
  const idx = currentItemIndex(session);
  const items = [...session.items];
  items[idx] = { ...items[idx], introduced: true };
  return advance({ ...session, items });
}

function updateItem(item, ok) {
  return ok
    ? { ...item, level: Math.min(MAX_LEVEL, item.level + 1), wrong: false }
    : { ...item, level: Math.max(0, item.level - 1), wrong: true };
}

/** Antwort auf die aktuelle Abfrage werten (ohne weiterzugehen) */
export function answerQuestion(session, ok) {
  const idx = currentItemIndex(session);
  const items = [...session.items];
  items[idx] = updateItem(items[idx], ok);
  return { ...session, items, tries: session.tries + 1 };
}

/** Nach der Rückmeldung zur nächsten Aufgabe */
export function nextQuestion(session) {
  return advance(session);
}

/**
 * Großen Test werten. results: { [itemIndex]: true/false }.
 * Gibt { session, passed, errors } zurück; danach continueAfterTest.
 */
export function gradeTest(session, results) {
  const items = session.items.map((it, idx) =>
    idx in results ? updateItem(it, results[idx]) : it
  );
  const errors = session.queue.filter((idx) => !results[idx]).length;
  const passed = errors <= allowedTestErrors(session.queue.length);

  let next = { ...session, items };
  if (passed) {
    if (session.testBlock === "final") {
      next.finalPassed = true;
    } else if (!session.passed.includes(session.testBlock)) {
      next.passed = [...session.passed, session.testBlock];
    }
  }
  return { session: next, passed, errors };
}

/** Nach dem Test weiter üben (der Zähler bis zum nächsten Test startet neu) */
export function continueAfterTest(session) {
  return nextStep({ ...session, tries: 0, testBlock: null });
}

/**
 * Anteil am Wochenziel: jeder bestandene Test (Blöcke + Abschluss) zählt gleich viel.
 */
export function topicWeekProgress(session, weeklyTarget) {
  const tests = session.blockKeys.length + 1;
  const done = session.passed.length + (session.finalPassed ? 1 : 0);
  return done === tests ? weeklyTarget : Math.round(((done * weeklyTarget) / tests) * 1000) / 1000;
}
